import {
  Case,
  Patient,
  QuizResultRegistration,
  Task,
  TaskWithRegistrationCount,
  TaskWorkgroup,
  Workgroup,
} from '@/models';
import {
  AuthService,
  CaseService,
  ErrorService,
  ImageService,
  PatientService,
  QuizResultsService,
  StaticSiteService,
} from '@/services';
import { Location } from '@angular/common';
import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { DomSanitizer } from '@angular/platform-browser';
import { Router } from '@angular/router';
import { IframeDialogComponent } from '../../components/iframe-dialog/iframe-dialog.component';
import { TaskComponent } from '../../components/task/task.component';

@Component({
  selector: 'app-case',
  templateUrl: './case.component.html',
  styleUrls: ['./case.component.scss'],
  standalone: false,
})
export class CaseComponent implements OnInit, OnChanges {
  @Input() id: string;
  @Input('padding-top') paddingTop: boolean = true;
  @Input('hide-iframe') hideIframe: boolean;

  case: Case;
  quizResult: QuizResultRegistration;
  teacher: boolean = false;
  isUserSuperAdmin: boolean = false;
  studentWorkgroups: Workgroup[];
  loading: boolean = false;
  iframeSafeUrl: any;
  toOpenTask: string;
  toOpenWorkgroup: string;
  patientImageUrl: string;
  tasksLoading: boolean = false;

  patientLoading: boolean = false;
  constructor(
    private caseService: CaseService,
    private authService: AuthService,
    private router: Router,
    private sanitizer: DomSanitizer,
    public dialog: MatDialog,
    private quizResultsService: QuizResultsService,
    private imageService: ImageService,
    private _location: Location,
    private errorService: ErrorService,
    private patientService: PatientService,
    private staticSiteService: StaticSiteService
  ) {
    const navigation = this.router.currentNavigation();
    if (navigation) {
      const state = navigation.extras.state as {
        taskId: string;
        workgroupId: string;
      };
      if (state) {
        this.toOpenTask = state.taskId;
        this.toOpenWorkgroup = state.workgroupId;
      }
    }
  }

  ngOnInit(): void {
    var id: string = '';
    if (this.id) {
      id = this.id;
    } else {
      id = this.router.url.split('/')[2];
    }
    this.loading = true;
    this.patientLoading = true;
    this.caseService.getCaseById(id).subscribe(
      _case => {
        this.teacher = this.authService.isUserTeacher() || this.authService.isUserAdmin();
        this.isUserSuperAdmin = this.authService.isUserSuperAdmin();
        this.loading = false;
        this.case = _case as Case;
        if (this.case.staticSite) {
          // An uploaded static site is the source of truth for the embed when set; iframeUrl is
          // otherwise a free-text custom URL. See StaticSiteService.buildPublicUrl.
          this.case.iframeUrl = this.staticSiteService.buildPublicUrl(this.case.staticSite.id);
        }
        void this.loadCasePatient();

        if (this.teacher) {
          this.tasksLoading = true;
          this.caseService.getCaseTasksForCaseId(id).subscribe(
            resp => {
              this.case.tasks = this.transformCaseTasks(this.case.tasks, resp as TaskWithRegistrationCount[]);
              this.tasksLoading = false;
              let task = this.case.tasks.find(x => x.id === this.toOpenTask);
              if (task) {
                this.openTask(task.id);
              }
            },
            err => this.errorService.onError(err)
          );
        }

        if (this.case.isQuiz) {
          this.case.iframeUrl += `?USER_ID=${this.authService.getLoggedInUserId()}&CASE_ID=${this.case.id}&TENANT_ID=${this.authService.getTenantIdFromToken()}`;
          this.quizResultsService.getResultsByCaseId(this.case.id).subscribe(
            resp => {
              this.quizResult = resp as QuizResultRegistration;
            },
            err => this.errorService.onError(err)
          );
        }
        this.sanitizeUrl(this.case.iframeUrl);
      },
      err => this.errorService.onError(err)
    );
    if (!this.teacher) {
      this.loadStudentData(id);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.id && !changes.id.firstChange) {
      this.ngOnInit();
    }
  }

  back() {
    this._location.back();
  }

  transformCaseTasks(basicTasks: Task[], taskWithRegCount: TaskWithRegistrationCount[]): Task[] {
    const tasks: Task[] = [...basicTasks];
    for (let task of tasks) {
      let taskWithCount = taskWithRegCount.find(x => x.task.id === task.id);
      if (taskWithCount) {
        task.amountDone = taskWithCount.registrationsCount;
        task.amountTotal = taskWithCount.registrationsTotal;
      }
    }
    return tasks;
  }

  openEditWorkgroups() {
    this.router.navigateByUrl(`casussen/${this.case.id}/werkgroepen`);
  }

  loadStudentData(id: string) {
    this.caseService.getCaseTaskWorkgroupsForCaseId(id).subscribe(
      resp => {
        var studentRegistrations = resp as TaskWorkgroup[];
        this.studentWorkgroups = [];
        for (let reg of studentRegistrations) {
          var foundWorkgroup = this.studentWorkgroups.find(w => w.id == reg.workgroup.id);
          if (foundWorkgroup) {
            reg.registrations.sort((a, b) => a.submittedTimestamp - b.submittedTimestamp);
            foundWorkgroup.taskWorkgroupRegistrations.push(reg);
          } else {
            var w = reg.workgroup;
            w.taskWorkgroupRegistrations = [];
            reg.registrations.sort((a, b) => a.submittedTimestamp - b.submittedTimestamp);
            w.taskWorkgroupRegistrations.push(reg);
            this.studentWorkgroups.push(w);
          }
        }
        this.studentWorkgroups.sort((a, b) => {
          if (a.name && b.name) {
            return a.name.localeCompare(b.name);
          } else if (a.isStudent) {
            return -1;
          } else if (b.isStudent) {
            return 1;
          } else {
            return 0;
          }
        });
        for (let w of this.studentWorkgroups) {
          w.taskWorkgroupRegistrations.sort((a, b) => a.task.order - b.task.order);
        }
      },
      err => this.errorService.onError(err)
    );
  }

  private async loadCasePatient(): Promise<void> {
    const { data } = await this.patientService.getPatientById(this.case.patient.id);
    this.patientLoading = false;
    if (data) {
      this.case.patient.properties = (data as unknown as Patient).properties;
      this.patientImageUrl = this.getImgUrl(this.case.patient);
    }
  }

  getImgUrl(patient: Patient): string {
    var imgId = patient.properties.find(x => x.property.name == 'foto')?.value;
    if (imgId) {
      return this.imageService.buildImageUrl(imgId);
    } else {
      return 'icon';
    }
  }

  openBackgroundUrl() {
    const originalUrl = this.case.patient.department.careInstitution.backgroundInformationUrl;
    const url = originalUrl.includes('http') ? originalUrl : 'https://' + originalUrl;
    this.dialog.open(IframeDialogComponent, {
      width: '100vw',
      maxWidth: '100vw',
      height: '100vh',
      panelClass: 'iframe-modal',
      data: url,
    });
  }

  sanitizeUrl(url: string): any {
    this.iframeSafeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
  }

  openIframeDialog() {
    // Opened as an in-page dialog rather than a new tab — a new tab is a fresh JS context with no
    // in-memory auth state (and, for uploaded static sites, the CDN's WAF requires a depd.online
    // Referer that only a same-document dialog reliably provides — see IframeDialogComponent).
    this.dialog.open(IframeDialogComponent, {
      width: '90vw',
      maxWidth: '90vw',
      height: '90vh',
      maxHeight: '90vh',
      panelClass: 'iframe-modal',
      data: this.case.iframeUrl,
    });
  }

  getPatientProperty(property: string) {
    return this.case.patient.properties.find(x => x.property.name == property)?.value;
  }

  countAmountTaskDone(task: Task): number {
    if (!task.registrations) return 0;
    var count: number = 0;
    for (let r of task.registrations) {
      if (r.registrations && r.registrations.length > 0) {
        r.registrations.sort((a, b) => a.submittedTimestamp - b.submittedTimestamp);
        if (r.registrations[r.registrations.length - 1].done) {
          count++;
        }
      }
    }
    return count;
  }

  openTask(taskId: string) {
    const task = this.case.tasks.find(x => x.id === taskId);
    const dialogRef = this.dialog.open(TaskComponent, {
      maxWidth: '95vw',
      width: '100%',
      maxHeight: '95vh',
      height: '95vh',
      data: {
        task: task,
        caseId: this.case.id,
        careInstitutionId: this.case.patient.department.careInstitution.id,
        caseRatingSystem: this.case.caseRatingSystem,
        toOpenWorkgroup: this.toOpenWorkgroup,
      },
    });
    dialogRef.afterClosed().subscribe(t => {
      this.toOpenTask = '';
      this.ngOnInit();
    });
  }

  openRegistration(taskId: string) {
    for (const wg of this.studentWorkgroups) {
      const clickedWorkgroup = wg.taskWorkgroupRegistrations.find(x => x.task.id === taskId);
      if (clickedWorkgroup) {
        const dialogRef = this.dialog.open(TaskComponent, {
          maxWidth: '60vw',
          width: '100%',
          maxHeight: '95vh',
          data: {
            registration: clickedWorkgroup,
            caseId: this.case.id,
            careInstitutionId: this.case.patient.department.careInstitution.id,
            patientId: this.case.patient.id,
          },
        });
        dialogRef.afterClosed().subscribe(reload => {
          if (reload) {
            this.loadStudentData(this.case.id);
          }
        });
        return;
      }
    }
  }

  editCase() {
    this.router.navigateByUrl(this.router.url + '/wijzigen');
  }

  checkIfQuizResultPassed(): boolean {
    var passingScore: number = +this.quizResult.passingScore;
    var score: number = +this.quizResult.score;
    return score >= passingScore;
  }
}
